import expandingNebula from "../../../assets/json/5-expanding-nebula.json"
import {Player} from "@lottiefiles/react-lottie-player";
import {getRandomColor} from "../../../utils/GetRandomColor";
import {MathJax, MathJaxContext} from "better-react-mathjax";
import IconText from "../../IconText";
import {faCloud} from "@fortawesome/free-solid-svg-icons";

export default function ExpandingNebula() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }
        
    return (
        <div className={"blog-body"}>
            <h1><IconText text={"5. Expanding Nebula"} icon={faCloud}/></h1>
            
            <h2>Question</h2>

            <div className={"blog-quote"}>
                <p>
                    You've escaped Commander Lambda's exploding space station along with numerous escape pods full of
                    bunnies. But -- oh no! -- one of the escape pods has flown into a nearby nebula, causing you to lose
                    track of it. You start monitoring the nebula, but unfortunately, just a moment too late to find
                    where
                    the pod went. However, you do find that the gas of the steadily expanding nebula follows a simple
                    pattern, meaning that you should be able to determine the previous state of the gas and narrow down
                    where you might find the pod.
                </p>
                <p>
                    From the scans of the nebula, you have found that it is very flat and distributed in distinct
                    patches,
                    so you can model it as a 2D grid. You find that the current existence of gas in a cell of the grid
                    is
                    determined exactly by its 4 nearby cells, specifically, (1) that cell, (2) the cell below it, (3)
                    the
                    cell to the right of it, and (4) the cell below and to the right of it. If, in the current state,
                    exactly 1 of those 4 cells in the 2x2 block has gas, then it will also have gas in the next state.
                    Otherwise, the cell will be empty in the next state.
                </p>
                <p>
                    For example, let's say the previous state of the grid (p) was: <br/>
                    .O.. <br/>
                    ..O. <br/>
                    ...O <br/>
                    O...
                </p>
                <p>
                    To see how this grid will change to become the current grid (c) over the next time step, consider
                    the
                    2x2 blocks of cells around each cell. Of the 2x2 block of [p[0][0], p[0][1], p[1][0], p[1][1]], only
                    p[0][1] has gas in it, which means this 2x2 block would become cell c[0][0] with gas in the next
                    time
                    step: <br/>
                    .O -> O <br/>
                    ..
                </p>
                <p>
                    Likewise, in the next 2x2 block to the right consisting of [p[0][1], p[0][2], p[1][1], p[1][2]], two
                    of
                    the containing cells have gas, so in the next state of the grid, c[0][1] will NOT have gas: <br/>
                    O. -> . <br/>
                    .O
                </p>
                <p>
                    Following this pattern to its conclusion, from the previous state p, the current state of the grid c
                    will be: <br/>
                    O.O <br/>
                    .O. <br/>
                    O.O
                </p>
                <p>
                    Note that the resulting output will have 1 fewer row and column, since the bottom and rightmost
                    cells do
                    not have a cell below and to the right of them, respectively.
                </p>
                <p>
                    Write a function solution(g) where g is an array of array of bools saying whether there is gas in
                    each
                    cell (the current scan of the nebula), and return an int with the number of possible previous states
                    that could have resulted in that grid after 1 time step. For instance, if the function were given
                    the
                    current state c above, it would deduce that the possible previous states were p (given above) as
                    well as
                    its horizontal and vertical reflections, and would return 4. The width of the grid will be between 3
                    and
                    50 inclusive, and the height of the grid will be between 3 and 9 inclusive. The solution will always
                    be
                    less than one billion (10^9).
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution([[True, True, False, True, False, True, False, True, True, False], [True, True,
                    False,
                    False, False, False, True, True, True, False], [True, True, False, False, False, False, False,
                    False,
                    False, True], [False, True, False, False, False, False, True, True, False, False]]) <br/>
                    Output: <br/>
                    11567 <br/>
                    <br/>
                    Input: <br/>
                    solution.solution([[True, False, True], [False, True, False], [True, False, True]]) <br/>
                    Output: <br/>
                    4 <br/>
                    <br/>
                    Input: <br/>
                    solution.solution([[True, False, True, False, False, True, True, True], [True, False, True, False,
                    False, False, True, False], [True, True, True, False, False, False, True, False], [True, False,
                    True,
                    False, False, False, True, False], [True, False, True, False, False, True, True, True]]) <br/>
                    Output: <br/>
                    254
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                This is a nondeterministic cellular automaton problem, meaning there is no direct way to find the
                previous
                states of a given grid. The problem with the classical brute force approach is that the number of
                possible
                previous states is huge. For a given 3x3 grid, there are $2^{16}=65536$ possible previous states and
                that is
                an optimistic scenario. Your RAM is not going to like this. So we need to do this search with pruning.
            </p>
            <p>
                If a cell has gas, its previous state has 4 possibilities: 1 cell has gas, others are empty.
            </p>
            <p>
                If a cell is empty, its previous state has 12 possibilities, there is no gas, 2 cells have gas, 3 cells
                have
                gas, and 4 cells have gas.
            </p>
            <pre><code className="language-python">{`true_predecessors = [
    # One true
    [[False, False], [False, True]],
    [[False, False], [True, False]],
    [[True, False], [False, False]],
    [[False, True], [False, False]],
]

false_predecessors = [
    # All false
    [[False, False], [False, False]],
    # Two false
    [[False, True], [True, False]],
    [[False, True], [False, True]],
    [[True, False], [True, False]],
    [[True, False], [False, True]],
    [[True, True], [False, False]],
    [[False, False], [True, True]],
    # One false
    [[False, True], [True, True]],
    [[True, False], [True, True]],
    [[True, True], [False, True]],
    [[True, True], [True, False]],
    # All true
    [[True, True], [True, True]],
]`}</code></pre>
            <p>
                Let's assume that given grid is:
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`$$
                    \\begin{bmatrix}
                    O & . & O \\\\
                    . & O & . \\\\
                    O & . & O \\\\
                    \\end{bmatrix}
                    $$`}
                </MathJax>
            </MathJaxContext>
            <p>
                Let's convert it to a boolean for simplicity:
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`$$
                    \\begin{bmatrix}
                    True & False & True \\\\
                    False & True & False \\\\
                    True & False & True \\\\
                    \\end{bmatrix}
                    $$`}
                </MathJax>
            </MathJaxContext>
            <p>
                The grid is 3x3, so we will have a 4x4 grid for the previous state.
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`$$
                    \\begin{bmatrix}
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}
                    $$`}
                </MathJax>
            </MathJaxContext>
            <p>
                The first element of the given grid is <code>True</code>, we can start generating the previous states
                from
                there. Since
                there are 4 possibilities for the first element, we will have 4 different grids.
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\[ 1:
                    \\begin{bmatrix}
                    False & False & - & - \\\\
                    False & True & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}
                    %
                    ,
                    %
                    \\:\\:
                    %
                    2:
                    %
                    \\begin{bmatrix}
                    False & False & - & - \\\\
                    True & False & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}
                    \\]`}
                </MathJax>
            </MathJaxContext>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\[ 3:
                    \\begin{bmatrix}
                    True & False & - & - \\\\
                    False & False & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}
                    %
                    ,
                    %
                    \\:\\:
                    %
                    4:
                    %
                    \\begin{bmatrix}
                    False & True & - & - \\\\
                    False & False & - & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}
                    \\]`}
                </MathJax>
            </MathJaxContext>
            <p>
                Let's go one cell to the bottom of the given grid. The value is <code>False</code>, so it should have 12
                different possibilities. This is where the pruning comes in. If we check the first row of the possible
                previous states and match them with the generated grids, we can eliminate some of the possibilities.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={expandingNebula} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>
            
            <p>
                Applying this logic until the last row, we get 8 possibilities. Without restricting the search like
                this,
                there would be $2^{8}=256$ grids that we would need to check. List of the possible previous states at
                the
                moment if you are curious:
            </p>
            <pre><code className="language-python">{`[
[[False, False, None, None], [False, True, None, None], [True, False, None, None], [False, False, None, None]],
[[False, False, None, None], [False, True, None, None], [False, True, None, None], [False, False, None, None]],
[[False, False, None, None], [True, False, None, None], [True, False, None, None], [False, False, None, None]],
[[False, False, None, None], [True, False, None, None], [False, True, None, None], [False, False, None, None]],
[[True, False, None, None], [False, False, None, None], [False, False, None, None], [False, True, None, None]],
[[True, False, None, None], [False, False, None, None], [False, False, None, None], [True, False, None, None]],
[[False, True, None, None], [False, False, None, None], [False, False, None, None], [False, True, None, None]],
[[False, True, None, None], [False, False, None, None], [False, False, None, None], [True, False, None, None]]
]`}</code></pre>
            <p>
                With the same idea but with a twist on the algorithm, now we need to check the second column while
                generating the first two rows of the third column.
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{bmatrix}
                    - & (check \\: this) & (while \\: generating \\: this) & - \\\\
                    - & (check \\: this) & (while \\: generating \\: this) & - \\\\
                    - & - & - & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}`}
                </MathJax>
            </MathJaxContext>
            <p>
                Moving on to the third row, since we have three already generated neighbors, we only need to generate
                the
                remaining one with respect to its neighbors.
            </p>
            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{bmatrix}
                    - & - & - & - \\\\
                    - & (check \\: this) & (check \\: this) & - \\\\
                    - & (check \\: this) & (while \\: generating \\: this) & - \\\\
                    - & - & - & - \\\\
                    \\end{bmatrix}`}
                </MathJax>
            </MathJaxContext>
            <p>
                Looping through the columns and rows, we can find the possible previous states of the given grid.
                Returning
                the length of the list will give us the answer.
            </p>

            <h2>Code</h2>
            <pre><code className="language-python">{`true_predecessors = [
    # One true
    [[False, False], [False, True]],
    [[False, False], [True, False]],
    [[True, False], [False, False]],
    [[False, True], [False, False]],
]

false_predecessors = [
    # All false
    [[False, False], [False, False]],
    # Two false
    [[False, True], [True, False]],
    [[False, True], [False, True]],
    [[True, False], [True, False]],
    [[True, False], [False, True]],
    [[True, True], [False, False]],
    [[False, False], [True, True]],
    # One false
    [[False, True], [True, True]],
    [[True, False], [True, True]],
    [[True, True], [False, True]],
    [[True, True], [True, False]],
    # All true
    [[True, True], [True, True]],
]

def solution(g):
    current_state = g
    # Creating the array for the previous state, where the dimensions are the same as the current state's dimensions + 1
    previous_state = []

    for i in range(len(current_state) + 1):
        previous_state.append([None] * (len(current_state[0]) + 1))

    # Now we will check the first column of the current state and fill the first two columns of the previous state accordingly
    possible_previous_states = []

    if current_state[0][0]:
        for predecessor in true_predecessors:
            previous_state[0][0] = predecessor[0][0]
            previous_state[1][0] = predecessor[1][0]
            previous_state[0][1] = predecessor[0][1]
            previous_state[1][1] = predecessor[1][1]
            possible_previous_states.append([list(sublist) for sublist in previous_state])
    else:
        for predecessor in false_predecessors:
            previous_state[0][0] = predecessor[0][0]
            previous_state[1][0] = predecessor[1][0]
            previous_state[0][1] = predecessor[0][1]
            previous_state[1][1] = predecessor[1][1]
            possible_previous_states.append([list(sublist) for sublist in previous_state])

    # Now we check current_state[1][0] and depending on the values in the possible_previous_states we will make a decision from predessors

    for i in range(1, len(current_state)):
        new_possible_previous_states = []
        for previous_state in possible_previous_states:
            common_row = [previous_state[i][0], previous_state[i][1]]
            if current_state[i][0]:
                for predecessor in true_predecessors:
                    if predecessor[0] == common_row:
                        new_state = [list(sublist) for sublist in previous_state]
                        new_state[i + 1][0] = predecessor[1][0]
                        new_state[i + 1][1] = predecessor[1][1]
                        new_possible_previous_states.append(new_state)
            else:
                for predecessor in false_predecessors:
                    if predecessor[0] == common_row:
                        new_state = [list(sublist) for sublist in previous_state]
                        new_state[i + 1][0] = predecessor[1][0]
                        new_state[i + 1][1] = predecessor[1][1]
                        new_possible_previous_states.append(new_state)
        possible_previous_states = new_possible_previous_states

    # Now we will do the same thing until we reach the last column of the current state
    # We need to do this without hardcoding the indexes, since the dimensions of the current state can change
    # But there is a trick here,
    # While generating the third column, if we are genearting the first step (first two rows), checking the common column will be enough
    # But if we are generating the other rows we need to check the other three

    for i in range(len(current_state[0])):
        for j in range(len(current_state)):
            new_possible_previous_states = []
            if j == 0:
                for previous_state in possible_previous_states:
                    common_column = [previous_state[j][i], previous_state[j+1][i]]
                    if current_state[j][i]:
                        for predecessor in true_predecessors:
                            if predecessor[0][1] == common_column[0] and predecessor[1][1] == common_column[1]:
                                new_state = [list(sublist) for sublist in previous_state]
                                new_state[j][i+1] = predecessor[0][0]
                                new_state[j+1][i+1] = predecessor[1][0]
                                new_possible_previous_states.append(new_state)
                    else:
                        for predecessor in false_predecessors:
                            if predecessor[0][1] == common_column[0] and predecessor[1][1] == common_column[1]:
                                new_state = [list(sublist) for sublist in previous_state]
                                new_state[j][i+1] = predecessor[0][0]
                                new_state[j+1][i+1] = predecessor[1][0]
                                new_possible_previous_states.append(new_state)
                possible_previous_states = new_possible_previous_states
            else:
                for previous_state in possible_previous_states:
                    common_elements = [previous_state[j][i], previous_state[j+1][i], previous_state[j][i+1]]
                    if current_state[j][i]:
                        for predecessor in true_predecessors:
                            if predecessor[0][1] == common_elements[0] and predecessor[1][1] == common_elements[1] and predecessor[0][0] == common_elements[2]:
                                new_state = [list(sublist) for sublist in previous_state]
                                new_state[j][i+1] = predecessor[0][0]
                                new_state[j+1][i+1] = predecessor[1][0]
                                new_possible_previous_states.append(new_state)
                    else:
                        for predecessor in false_predecessors:
                            if predecessor[0][1] == common_elements[0] and predecessor[1][1] == common_elements[1] and predecessor[0][0] == common_elements[2]:
                                new_state = [list(sublist) for sublist in previous_state]
                                new_state[j][i+1] = predecessor[0][0]
                                new_state[j+1][i+1] = predecessor[1][0]
                                new_possible_previous_states.append(new_state)
                possible_previous_states = new_possible_previous_states

    # Removing the states appearing more than once
    possible_previous_states = list(set([tuple([tuple(sublist) for sublist in result]) for result in possible_previous_states]))

    return len(possible_previous_states)`}</code></pre>
        </div>
    )
}